/**
 * Centralised Contentful content type IDs.
 * Import from here — never use magic strings in page routes or actions.
 */
export const CONTENT_TYPE_ORGANIC_PAGE = 'pageOrganicPage';
export const CONTENT_TYPE_PAID_LANDING_PAGE = 'pagePaidLandingPage';
export const CONTENT_TYPE_ARTICLE = 'pageArticle';
export const CONTENT_TYPE_ARTICLE_CATEGORY = 'articleCategory';
export const CONTENT_TYPE_LINK = 'link';
export const CONTENT_TYPE_OFFERS = 'offers';
export const CONTENT_TYPE_OFFER = 'offer';
export const CONTENT_TYPE_LOCATION_PAGE = 'locationPage';
export const CONTENT_TYPE_MEDIUM_IMAGE_HERO_BANNER = 'mediumImageHeroBanner';
export const CONTENT_TYPE_PLP_HERO_BANNER = 'plpHeroBanner';
export const CONTENT_TYPE_LOCATION_HERO = 'locationHero';
export const CONTENT_TYPE_LOCATION_TWO_COLUMN_TEXT = 'locationTwoColumnText';
export const CONTENT_TYPE_LOCATION_FIELDS = 'locationFields';
export const CONTENT_TYPE_REDIRECT = 'redirect';

/**
 * Base URL paths for application routes.
 */
export const LEARNING_HUB_BASE_PATH = '/about-us/blog';
export const PAID_LANDING_PAGE_BASE_PATH = '/ready';

/**
 * Slug for the homepage organic page in Contentful.
 * The public route for this page should be "/".
 */
export const HOMEPAGE_SLUG = 'homepage';

/**
 * Key pages shown in the HTML Sitemap "Key Pages" section.
 * Each entry maps a Contentful slug to a human-readable display label.
 * Brand-specific pages (e.g. "Why Brand") are resolved via brandCode at runtime.
 * Slugs must match exactly as stored in Contentful (no leading/trailing slashes).
 */
export const HTML_SITEMAP_KEY_PAGES: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'homepage', label: 'Home' },
  { slug: 'contact', label: 'Contact Us' },
  { slug: 'about-us', label: 'About Us' },
  { slug: 'reviews', label: 'Reviews' },
  { slug: 'preferred-brands', label: 'Preferred Brands' },
  { slug: 'community', label: 'In the Community' },
  { slug: 'about-us/blog', label: 'Blog' },
  { slug: 'ways-to-save', label: 'Ways to Save' },
  { slug: 'comfort360', label: 'Comfort360 Membership' },
  { slug: 'offers-and-rebates', label: 'Offers & Rebates' },
  { slug: 'financing', label: 'Financing' },
  { slug: 'privacy-policy', label: 'Privacy Policy' },
  { slug: 'accessibility', label: 'Accessibility' },
] as const;

/**
 * SEO fields
 */
export const seoFields = [
  'fields.metaTitle',
  'fields.metaDescription',
  'fields.canonicalOverride',
  'fields.ogTitle',
  'fields.ogDescription',
  'fields.noFollow',
  'fields.noIndex',
];

/**
 * Top-level fields of the organic page entry unused by location city pages.
 * Safe to strip: city page SEO (metaTitle, metaDescription) comes from
 * getCachedLocationFields, not from the organic page.
 */
export const ORGANIC_PAGE_SEO_FIELDS_TO_STRIP = new Set([
  'slug',
  'metaTitle',
  'seoTitle',
  'metaDescription',
  'seoDescription',
  'noIndex',
  'noindex',
  'noFollow',
  'nofollow',
  'createdAt',
]);

export const ARTICLE_FIELDS_TO_STRIP = [];
export const CATEGORY_FIELDS_TO_STRIP = ['createdAt'];
export const LOCATION_PAGE_FIELDS_TO_STRIP = ['createdAt'];
export const PAID_LANDING_PAGE_FIELDS_TO_STRIP = ['createdAt'];
export const ORGANIC_PAGE_FIELDS_TO_STRIP = ['createdAt'];

/** Field selection for search index — articles (include: 0, primitives + rich text on entry). */
export const SEARCH_ARTICLE_SELECT = [
  'sys.id',
  'fields.slug',
  'fields.title',
  'fields.articleContent',
];

/** Field selection for search index — organic pages (include: 4 for pageComponents). SEO meta omitted. */
export const SEARCH_ORGANIC_SELECT = ['sys.id', 'fields.slug', 'fields.pageComponents'];

/** Field selection for search index — location pages (include: 0, same as getLocationPageBySlug). */
export const SEARCH_LOCATION_SELECT = [
  'sys.id',
  'fields.slug',
  'fields.localizedIntro',
  'fields.endingCopy',
];

/** Static map: kebab-case state name → 2-letter abbreviation */
export const STATE_CODE_MAP: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new-hampshire': 'NH',
  'new-jersey': 'NJ',
  'new-mexico': 'NM',
  'new-york': 'NY',
  'north-carolina': 'NC',
  'north-dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode-island': 'RI',
  'south-carolina': 'SC',
  'south-dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west-virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district-of-columbia': 'DC',
};

/** Map of city name overrides for formatting city names in location page titles and headings. */
export const CITY_WORD_OVERRIDES: Record<string, string> = {
  naec: 'NAEC',
};
